// Case study content lives here so the index and the detail page stay in sync.
// Section types: 'text' (paragraphs), 'list' (bullets), 'terms' (label + body),
// 'callout' (the one line worth pulling out), 'image' (screenshot + caption),
// 'gallery' (transparent cut-outs side by side; `ratio` is each one's width / height).
// An optional `hero` image sits under the title, above the meta card.

import curriculumHero from '../media/curriculum-hero.webp';
import curriculumResults from '../media/curriculum-results.webp';
import solaraHero from '../media/solara-hero.webp';
import solaraYourSky from '../media/solara-your-sky.webp';
import solaraWidgetFocus from '../media/solara-widget-focus.webp';
import solaraWidgetAffirmation from '../media/solara-widget-affirmation.webp';

const caseStudies = [
  {
    id: 'curriculum-mapping',
    title: 'Mapping a Curriculum with AI',
    org: 'Yale School of Management',
    kind: 'Product case study',
    year: '2026',
    tech: 'LLM APIs • Python • FastAPI • React',
    summary:
      'A tool that turns a large collection of course syllabi into an evidence-backed table of where themes are taught across the school. Deciding what counts as evidence, how much judgment to hand the model, and where a human expert had to stay in the loop was the biggest challenge.',
    hero: {
      src: curriculumHero,
      alt: 'The Syllabus Analysis tool answering a search for sessions about sustainability: a written summary of the strongest matches, a course-by-course grid of matched and borderline sessions, and the start of the evidence table.'
    },
    meta: [
      { label: 'Role', value: 'AI Research Intern — product, UX, and engineering' },
      { label: 'User', value: "SOM Dean's Office" },
      { label: 'Status', value: 'Working pilot, undergoing Yale Data Governance review' }
    ],
    pdf: '/Mapping_a_Curriculum_with_AI.pdf',
    sections: [
      {
        type: 'text',
        body: [
          'I built a tool that turns a large collection of course syllabi into a queryable corpus that returns tables of where themes are taught across Yale School of Management (SOM). The more difficult part was deciding what the system should count as evidence, how much judgment to give the model, and where a human expert still needed to stay in the loop.'
        ]
      },
      {
        type: 'text',
        heading: 'The problem',
        body: [
          'My mentor, Gabriel Rossi, Assistant Dean at Yale SOM, wanted to map the school’s curriculum. The resource we had most consistently across courses was syllabi, so the first question was **what part of a syllabus actually represents the curriculum?**',
          'Syllabi contain plenty of information that is useful to students but not useful for curriculum mapping such as attendance rules, grading policies, office hours, plagiarism language, and other administrative details. Working together, we decided that session information was the best signal.',
          'The old workflow was effectively nonexistent. Someone could manually read every syllabus and categorize every course, but the catalog changes constantly and the amount of effort required made it unrealistic to repeat often enough to be useful.'
        ]
      },
      {
        type: 'text',
        heading: 'First instinct',
        body: [
          'I initially thought this was a retrieval problem, so my first instinct was to implement a standard RAG pipeline to index the syllabi, retrieve relevant passages, and answer questions from them.',
          'That worked for finding text, but it missed the real issue. A question like “Which courses teach artificial intelligence?” is subjective. Does one class session about AI make a course relevant? What if AI appears inside a case without being the main topic? What if the connection is implied rather than stated directly?',
          'The product was not really being asked to retrieve information. It had to **make a judgment that should roughly align with how my user would make the same judgment manually.**',
          'Instead of treating the model like a search engine, I began designing around an explicit interpretation of each theme, evidence from the syllabus, and a way for the user to see uncertainty.'
        ]
      },
      {
        type: 'terms',
        heading: 'The product principles',
        items: [
          {
            label: 'Discernment.',
            body: 'The model needed a working definition of a theme that was close to the definition my mentor would actually use.'
          },
          {
            label: 'Honesty.',
            body: 'If something might be relevant, I would rather it surface it with lower confidence than silently drop it from the curriculum map.'
          },
          {
            label: 'Evidence.',
            body: 'Every result needed to show why it was included, using evidence from the syllabus itself.'
          },
          {
            label: 'Simplicity.',
            body: 'My mentor was the target user, but I wanted the interface to make sense to another administrator without requiring any technical understanding.'
          },
          {
            label: 'Familiar output.',
            body: 'A thematic table became a core part of the product because it matched a format the Dean’s Office already used and made it easy to scan across many courses quickly.'
          }
        ]
      },
      {
        type: 'text',
        heading: 'Coverage vs. false certainty',
        body: [
          'The product decision I am most proud of was how I handled borderline results. One option was to make the model very strict. That would create a cleaner list at the cost of potentially missing a theme-relevant course because the model was not confident enough to include it.',
          'The other option was to let the definition be somewhat broader, surface more candidates, and make the system explicit about confidence. My mentor and I chose the second approach.'
        ]
      },
      {
        type: 'callout',
        body:
          'If it could narrow 80 syllabi down to 10 plausible courses, even if two were ultimately rejected, that was still more useful than asking someone to read all 80 from scratch.'
      },
      {
        type: 'text',
        body: [
          'The model’s job was really to reduce the search space and explain its reasoning, while the human’s job was to make the final call.'
        ]
      },
      {
        type: 'text',
        heading: 'Avoiding overengineering',
        body: [
          'At one point I worried that we were overfitting the system to this one use case. I considered making the entire syllabus corpus queryable so a user could ask anything such as attendance policies, exam formats, grading structures, reading loads, or whatever else happened to be in the documents.',
          'I eventually realized that making the entire corpus queryable introduced too much irrelevant context. Administrative details like attendance policies and grading rules cluttered the prompt and degraded the model’s outputs, so I narrowed the system around the parts of each syllabus most useful for curriculum mapping.',
          'I made a similar decision on external research. The system could have spawned agents to research concepts mentioned in a syllabus and build more elaborate interpretations. I chose not to because the users are professionals who should still make the final call. Automating this would add cost and complexity while making the final judgment harder to verify.'
        ]
      },
      {
        type: 'text',
        heading: 'User feedback made the interface simpler',
        body: [
          'Early versions exposed too much of the machinery underneath the product. There were extra buttons, dropdowns, extraction states, corpus processing metrics, and other information that was useful to me while building but not at all for the user.',
          'Gabriel’s feedback forced me to separate *developer information* from *user information*. I simplified the interface around that idea, so that when the system is working, the complexity stays out of the way. But when something fails, the product explains the failure in a way thats helpful to the user.'
        ]
      },
      {
        type: 'text',
        heading: 'Designing around AI unreliability',
        body: [
          'Reliability mattered because if the tool missed a course, it would be directly reduce the accuracy of the output. If a curriculum map says a theme does not appear somewhere, the user needs to trust that the system actually looked, so I designed the product around traceability.',
          'Results include supporting evidence from the syllabus, and the system checks that the material it was supposed to evaluate actually received a judgment. My goal was to make its judgments inspectable enough that a human could catch a poorly classified class rather than never know of a completely ommited one.'
        ]
      },
      {
        type: 'image',
        src: curriculumResults,
        alt: 'The results table. Each row lists a course, the matching session, a one-line summary, a match strength such as strong or substantive, a link to the exact syllabus page, and a Drop control.',
        caption:
          'Every row links to the syllabus page it came from, and the reviewer can drop any result they disagree with.'
      },
      {
        type: 'text',
        heading: 'Reliability over speed',
        body: [
          'One constraint that ended up working in my favor was that Gabriel didn’t need an instant answer. Even if the tool took hours or ran overnight to process the full curriculum, it was still dramatically faster than reading dozens of syllabi manually. I used that tolerance to optimize for reliability instead of speed.',
          'Rather than send the entire curriculum through one long model call, I split the work into batches. Each batch could be processed and checked independently, which reduced the chance that the model would lose track of material or hallucinate under a huge context. The system only combined the results after every batch had returned a judgment.'
        ]
      },
      {
        type: 'text',
        heading: 'Where it is now',
        body: [
          'The tool is a working pilot that Gabriel is actively testing with larger sets of syllabi. A single corpus can handle around 80 syllabi and return evidence-linked results showing where a theme appears across courses.',
          'It has been approved internally and is now moving through Yale’s Data Governance and security review process before broader deployment. After that process, the plan is to explore broader use within SOM and potentially with other parts of Yale.'
        ]
      },
      {
        type: 'list',
        heading: 'What I would do differently',
        intro:
          'If I started again, I would ask more product questions before writing code. Early on I was thinking about what retrieval architecture to use, how general the system should be, and whether I was overfitting to one user. Towards the end I realized the most important questions were:',
        items: [
          'What decision is the user actually trying to make?',
          'What information do they need to make it?',
          'Which parts should be automated?',
          'Which parts shouldn’t?'
        ],
      },
      {
        type: 'text',
        heading: 'The takeaway',
        body: [
          'The part of this project I found most exciting was building something for a real user, watching them use it, and changing the product when my assumptions were wrong.',
          'The biggest lesson was that a useful AI often shouldn\'t automate the final decision. In this case, the better product was the one that made a previously impossible review process manageable while still leaving the expert in control.'
        ]
      }
    ]
  },
  {
    id: 'solara',
    title: 'Building Solara',
    org: 'Helvet Studios',
    kind: 'Product case study',
    year: '2026',
    tech: 'SwiftUI • Supabase • APIs',
    summary:
      'A personalized affirmation app that uses astrology to make daily affirmations feel personal. The recurring decision was not what to build — astrology has endless depth — it was what to leave out so the product stayed an affirmation app instead of drifting into another horoscope app.',
    hero: {
      src: solaraHero,
      alt: 'Three Solara notifications on an iPhone lock screen at 11:11, over a sunset sky: a reminder of today’s intention and two affirmations.'
    },
    meta: [
      { label: 'Role', value: 'Founder & Product Lead — product, UX, and engineering' },
      { label: 'User', value: 'Women 18–35 interested in astrology and affirmations' },
      { label: 'Status', value: 'Private beta, preparing for public App Store launch' },
      {
        label: 'Site',
        value: 'helvetstudios.com/solara',
        href: 'https://www.helvetstudios.com/solara'
      }
    ],
    link: 'https://www.helvetstudios.com/solara',
    linkLabel: 'Visit Solara',
    pdf: '/Building_Solara.pdf',
    sections: [
      {
        type: 'text',
        body: [
          'I built Solara, a personalized affirmation app that uses astrology as a way to make daily affirmations feel more personal. The idea came from seeing generic affirmation apps succeed alongside astrology and spirituality apps, but very few products combined the two without turning into a horoscope app.'
        ]
      },
      {
        type: 'text',
        heading: 'The opportunity',
        body: [
          'I did not want to compete directly with established astrology apps. If someone already trusted an app for horoscopes and chart readings, I would already be at a disadvantage in having to prove they should replace it with mine.',
          'The more interesting opportunity was adjacent. Solara could live alongside those apps by focusing on **short affirmations tied to their chart**, delivered through a feed, notifications, and widgets throughout the day.',
          'Before building, I took a small convenience sample of ~20 women across five colleges and looked at how astrology content performed on TikTok. I was initially targeting women ages 18–35. Most of the people I spoke with were friends of friends rather than people I knew directly, which helped me get outside my immediate circle even though the sample was still small. The combination of those conversations, the amount of attention astrology content received online, and the success of existing astrology apps gave me enough confidence to test the idea.'
        ]
      },
      {
        type: 'text',
        heading: 'Keeping it affirmation-first',
        body: [
          'As I kept building, the biggest risk was losing the identity of the product. Astrology has almost endless depth: houses, transits, compatibility, chart interpretation, journaling, and more. To prevent Solara from becoming another general astrology app, I kept asking whether each feature made **the affirmation experience** stronger.',
          'That led to three feeds based on the user’s Sun, Moon, and Rising signs. Each feed feels personal for a different reason, but it all centers around affirmations.'
        ]
      },
      {
        type: 'text',
        heading: 'Designing the feed',
        body: [
          'I chose a full-screen vertical feed because it already felt natural to the audience I was targeting. Leading affirmation apps were using similar interactions, and my users were already familiar with that rhythm from TikTok.',
          'I considered swipeable cards, but they felt more playful than the tone I wanted. I wanted Solara to feel polished enough that a younger user would enjoy it without making an older user in the same demographic feel like the app was childish or embarrassing to open in public.'
        ]
      },
      {
        type: 'text',
        heading: 'The free tier',
        body: [
          'Researching competitors, I noticed users leaving negative app reviews and TikTok comments whenever an app put a hard paywall after a long run of onboarding screens. That felt like massive damage to the top of the funnel, destroying the app’s reputation with potential new users before they ever try it.',
          'So the Sun feed and daily notifications (up to 3x) are fully usable for free. Moon and Rising feeds, additional themes, and other personalization features create reasons to upgrade. I also placed occasional previews from the locked feeds inside the free experience, so users could see what premium added without being interrupted by constant hard sells.',
          'For paid pricing, I wanted to push users toward the annual plan, so I made the monthly plan relatively more expensive and created a limited founders offer for early users. The goal was to give early users a reason to make a decision rather than assume the same price would always be available. I also placed that offer **after** someone had already built a profile and seen their personalized experience. My thinking was that the offer would mean more once they had invested time in making the app feel like theirs.'
        ]
      },
      {
        type: 'callout',
        body:
          'Three of the first ten beta users chose the paid annual plan. The sample is too small to call that a conversion rate, but it was enough to show that some users would pay for the product rather than only say they liked the idea.'
      },
      {
        type: 'text',
        heading: 'Onboarding',
        body: [
          'Solara needs personal information to feel personal, but every question adds friction. I tried to find a middle ground where onboarding collected enough to make the experience feel tailored without asking questions that did not change anything.',
          'The flow asks about the user’s familiarity with astrology, how much they believe in it, what they are looking for, and optionally the birth information needed to calculate their chart. I let users calculate their chart before paying, even though that creates cost for users who may never convert because by the time someone reaches the paywall, I want them to have already built a profile, seen their chart, and understood what the product is personalizing for them.'
        ]
      },
      {
        type: 'text',
        heading: 'What users changed',
        body: [
          'Putting the app in people’s hands showed me that features I thought were intuitive were not always obvious.',
          'The biggest example was the daily ritual. I expected users to discover it and naturally come back to it, and many did not. I moved the ritual directly onto the home experience and then built *Your Sky*, a constellation that fills in as users complete the ritual over time. That made the behavior easier to discover and gave repeat use a visible payoff.'
        ]
      },
      {
        type: 'image',
        src: solaraYourSky,
        alt: 'Solara’s home screen: Your Sky, a Virgo constellation with a star for each day of the week the ritual was completed, above Today’s Ritual with three steps: Affirm, Set your intention, and Write a reflection.'
      },
      {
        type: 'text',
        body: [
          'Themes surprised me in the opposite direction. I originally thought of them as mostly aesthetic, but almost every early user experimented with changing the app’s theme once I added it. Some eventually returned to the default, which was still useful feedback.',
          'I also learned not to react to every individual comment. I looked for repeated behavior or repeated feedback before changing anything, because I did not want to overfit Solara to whichever person I had spoken with most recently.'
        ]
      },
      {
        type: 'text',
        heading: 'Retention',
        body: [
          'Solara is supposed to be useful every day, so I built lots of ways for the product to keep showing up without requiring the user to remember to open it. Users choose how many affirmation notifications they want during onboarding. Widgets put a daily focus or affirmation directly on the home screen. Your Sky gives the daily ritual a sense of progress, and liked affirmations shape what the user sees later.'
        ]
      },
      {
        type: 'gallery',
        images: [
          {
            src: solaraWidgetFocus,
            alt: 'Small home screen widget showing the daily focus, Awareness.',
            ratio: 486 / 482
          },
          {
            src: solaraWidgetAffirmation,
            alt: 'Medium home screen widget with an affirmation for the daily focus: “I am grounded, centered, and aligned with my highest self.”',
            ratio: 695 / 325
          }
        ]
      },
      {
        type: 'text',
        heading: 'Privacy vs. product capability',
        body: [
          'One of my biggest mistakes was trying to keep almost everything local on the device, because I wanted users to feel their data was private and to be able to use the app without an internet connection.',
          'That became a problem as the product grew. Scheduled notifications, synchronized seen states, account persistence, and other behavior were difficult to support without a backend. I eventually moved the app to Supabase to use edge functions amongnst other things.'
        ]
      },
      {
        type: 'callout',
        body:
          'The lesson was that I had optimized for one requirement before I understood the rest of the product.'
      },
      {
        type: 'text',
        body: [
          'Sensitive information still gets treated carefully, and affirmation seen status is cached so users can read offline.'
        ]
      },
      {
        type: 'text',
        heading: 'Why I kept AI out of the core experience',
        body: [
          'I chose a manually written content pool over dynamic LLM-generated affirmations. It gives me more control over tone, repetition, and which concepts appear in each feed, avoids unnecessary model usage, and makes the content easier to improve deliberately.',
          'I made the same kind of decision with AI journal summaries and a deeper house system. Journal summaries felt too invasive for the value they added, and the house system felt unnecessary until users actually showed a need for it.'
        ]
      },
      {
        type: 'text',
        heading: 'What I would do differently',
        body: [
          'If I started Solara again, I would set a much harder MVP boundary and ship sooner.',
          'I spent too much time tuning the product before enough real users had touched it. This was my first time shipping a consumer product people might actually pay for, and I was worried users would reject it unless it felt complete. That meant too many early decisions were based on my own judgment instead of user behavior.'
        ]
      },
      {
        type: 'text',
        heading: 'What I’m measuring next',
        body: [
          'Right now I care most about the top of the funnel. Is the positioning is strong enough to make people download the app, and is onboarding is strong enough to turn those downloads into paid users? I am also testing short-form content as the first acquisition channel.',
          'After that, retention becomes the harder question. Will users keep notifications enabled, return to the ritual, and continue paying once the novelty wears off. The first sign that Solara is working will be users consistently choosing a paid plan and then keeping the app around long enough that it becomes part of their routine.'
        ]
      },
      {
        type: 'text',
        heading: 'The takeaway',
        body: [
          'The part of Solara I am most proud of is keeping the app focused as I kept finding more things I could build. The strongest product decisions I made were about deciding whether something actually strengthened the affirmation experience, then watching how users behaved and changing the product when my assumptions were wrong.'
        ]
      }
    ]
  },
  {
    id: 'locked-postmortem',
    title: 'Locked',
    org: 'Shelved · Tsai CITY Launchpad',
    kind: 'Post-mortem',
    year: '2026',
    tech: 'React Native • TypeScript • Supabase',
    summary:
      'A social accountability app I built and then chose not to launch. The product worked but the distribution didn’t exist yet.',
    meta: [
      { label: 'Role', value: 'Founder — product and engineering' },
      { label: 'Backing', value: 'Tsai CITY Launchpad' },
      { label: 'Status', value: 'Shelved, not abandoned' }
    ],
    sections: [
      {
        type: 'text',
        body: [
          'Locked was a social accountability app exploring how identity, visibility, and social pressure shape discipline. I built a working MVP: camera check-ins, a friend system, profile flows, notifications, and a social feed. It was backed by Tsai CITY Launchpad.',
        ]
      },
      {
        type: 'text',
        heading: 'Why I stopped',
        body: [
          'Social apps are hard to solve because of the cold start problem. The product is worthless to the first user and valuable to the thousandth, and nothing about the code closes that gap. An accountability app is a strict version of this: it is not just that a new user has no friends on it, it is that the entire mechanic — being seen — does nothing until their actual friends are there too.',
          'I did not have a distribution network. No audience, no channel, no existing group of people I could turn on at once. Shipping into that means the app dies regardless of how good the check-in flow feels.'
        ]
      },
      {
        type: 'callout',
        body:
          'Launching a social product without a way to reach a first cohort is the constraint, and I did not have one yet.'
      },
      {
        type: 'text',
        heading: 'The research',
        body: [
          'Before deciding, I spent real time on how consumer social products actually get off the ground — a lot of it from Nikita Bier and his launch of Gas. What stuck with me was how little of it was about the app. The launch was the product: seeding school by school, engineering a dense network inside one place before touching the next, and building a mechanic that gives a brand new user something worth opening on the very first session rather than after they recruit five friends.',
          'Reading that against Locked made the gap obvious. I had built the thing you launch. I had not built the launch.'
        ]
      },
      {
        type: 'list',
        heading: 'What I took from it',
        items: [
          'For consumer social, I learned distribution is part of the product spec, not a later phase.',
          'The first-session experience has to work at network size zero, or the network never gets to size one.',
          'Knowing when to stop is a product skill. I would rather shelve this deliberately than spend another semester shipping features into an empty feed.'
        ]
      },
      {
        type: 'text',
        heading: 'Where it stands',
        body: [
          'I still think the idea is worth getting off the ground later. The app exists and works. Even distribution feels promising with the problem only needing to be solved in clusters of friend groups rather than with hundreds of users at once. I\'ll revisit this with more experience under my belt. If I still like it maybe I\'ll give it a second chance'
        ]
      }
    ]
  }
];

export default caseStudies;
